/* eslint-disable max-lines */
import { useCallback, useEffect, useRef, useState } from "react";
import { useArtworkForm, useArtworkImage } from "@design-lab/entities/artwork";
import { useSelectProductComposition } from "@design-lab/entities/composition";
import { useFetchPrintOnDemandProduct } from "@design-lab/entities/product";
import { validateDesignLabArtworkFile } from "common/artwork-file-types";
import type { Side } from "@design-lab/shared/types";
import type { UrlFlagsContext } from "@design-lab/widgets/design-lab-upload-artwork/model/url-flags.machine";
import type { printOnDemand } from "common/utils/models";
import { useIsInitialState } from "../useIsInitialState";
import {
  processAutoUpload,
  processDesignLibrary,
  processCanva,
  processAiDesign,
  processPdpOnly,
  type WorkflowHandlerParams
} from "./workflowHandlers";

export type WorkflowType =
  | "autoUpload"
  | "designLibrary"
  | "canva"
  | "aiDesign"
  | "pdpOnly"
  | "none";

export type UseWorkflowProcessorParams = {
  urlFlags: UrlFlagsContext | null;
  productsMap: Map<string, printOnDemand.getProduct.Composition & { catalogId: string }>;
  productQueries: Array<{ data: printOnDemand.getProduct.Response | undefined }>;
  isReady: boolean;
  onWorkflowComplete: () => void;
  onWorkflowError: (error: Error) => void;
};

export type UseWorkflowProcessorReturn = {
  activeWorkflow: WorkflowType;
  isProcessing: boolean;
  processWorkflow: () => Promise<void>;
  isDesignLibraryOpen: boolean;
  isCanvaOpen: boolean;
  isAiDesignOpen: boolean;
  handleLibrarySelection: (artworkId: number) => void;
  handleLibraryCancel: () => void;
  handleCanvaComplete: (file: File) => void;
  handleCanvaCancel: () => void;
  handleAiComplete: () => void;
  autoUploadFile: File | null;
  existingArtworkId: string | undefined;
};

export function useWorkflowProcessor(
  params: UseWorkflowProcessorParams
): UseWorkflowProcessorReturn {
  const { urlFlags, productQueries, isReady, onWorkflowComplete, onWorkflowError } = params;

  const { form: artworkForm } = useArtworkForm();
  const { uploadArtwork } = useArtworkImage();
  const { selectProductComposition } = useSelectProductComposition();
  const fetchProduct = useFetchPrintOnDemandProduct();
  const { isInitialState } = useIsInitialState();

  const [activeWorkflow, setActiveWorkflow] = useState<WorkflowType>("none");
  const [isProcessing, setIsProcessing] = useState(false);
  const [autoUploadFile, setAutoUploadFile] = useState<File | null>(null);
  const [existingArtworkId, setExistingArtworkId] = useState<string>();
  const [isDesignLibraryOpen, setIsDesignLibraryOpen] = useState(false);
  const [isCanvaOpen, setIsCanvaOpen] = useState(false);
  const [isAiDesignOpen, setIsAiDesignOpen] = useState(false);

  const hasProcessedRef = useRef(false);
  const originalArtworkRef = useRef<any>(null);

  // Determine active workflow from URL flags
  useEffect(() => {
    if (!urlFlags || !isReady || hasProcessedRef.current) return;

    let workflow: WorkflowType = "none";
    if (urlFlags.workflowFlags.hasDesignLibrary) {
      workflow = "designLibrary";
    } else if (urlFlags.workflowFlags.hasCanva) {
      workflow = "canva";
    } else if (urlFlags.workflowFlags.hasAiDesign) {
      workflow = "aiDesign";
    } else if (urlFlags.transientData.artworkUrls || urlFlags.transientData.uploadUrls) {
      workflow = "autoUpload";
    } else if (
      urlFlags.transientData.catalogNumber &&
      urlFlags.transientData.pdpQuantities &&
      urlFlags.transientData.pdpSizeData
    ) {
      workflow = "pdpOnly";
    }
    setActiveWorkflow(workflow);
  }, [urlFlags, isReady]);

  const processWorkflow = useCallback(async () => {
    if (hasProcessedRef.current || !isReady || activeWorkflow === "none") {
      return;
    }
    setIsProcessing(true);
    hasProcessedRef.current = true;

    const handlerParams: WorkflowHandlerParams = {
      urlFlags,
      productQueries,
      fetchProduct,
      selectProductComposition,
      uploadArtwork,
      artworkForm,
      isInitialState,
      setAutoUploadFile,
      setExistingArtworkId,
      setIsDesignLibraryOpen,
      setIsCanvaOpen,
      setIsAiDesignOpen,
      originalArtworkRef
    };

    try {
      if (activeWorkflow === "autoUpload") {
        await processAutoUpload(handlerParams);
        onWorkflowComplete();
      } else if (activeWorkflow === "designLibrary") {
        await processDesignLibrary(handlerParams);
        onWorkflowComplete();
      } else if (activeWorkflow === "canva") {
        await processCanva(handlerParams);
        onWorkflowComplete();
      } else if (activeWorkflow === "aiDesign") {
        await processAiDesign(handlerParams);
        onWorkflowComplete();
      } else if (activeWorkflow === "pdpOnly") {
        await processPdpOnly(handlerParams);
        onWorkflowComplete();
      } else {
        onWorkflowComplete();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Workflow processing error:", error);
      window.Honeybadger?.notify("Workflow processing error", {
        params: {
          error: error instanceof Error ? error.message : String(error),
          error_object: error instanceof Error ? error.toString() : String(error),
          stack_trace: error instanceof Error ? error.stack : null,
          workflow: activeWorkflow,
          urlFlags: urlFlags?.transientData
        }
      });
      onWorkflowError(error instanceof Error ? error : new Error("Workflow failed"));
    } finally {
      setIsProcessing(false);
    }
  }, [
    isReady,
    activeWorkflow,
    onWorkflowComplete,
    onWorkflowError,
    urlFlags,
    productQueries,
    fetchProduct,
    selectProductComposition,
    uploadArtwork,
    artworkForm,
    isInitialState
  ]);

  const handleLibrarySelection = useCallback(() => {
    setIsDesignLibraryOpen(false);
    onWorkflowComplete();
  }, [onWorkflowComplete]);

  const handleLibraryCancel = useCallback(() => {
    setIsDesignLibraryOpen(false);
    if (originalArtworkRef.current) {
      const targetSide = urlFlags?.transientData.side === "back" ? "back" : "front";
      if (targetSide === "back") {
        artworkForm.setValue("back", originalArtworkRef.current.back);
      } else {
        artworkForm.setValue("front", originalArtworkRef.current.front);
        artworkForm.setValue("frontLeftChest", originalArtworkRef.current.frontLeftChest);
      }
    }
    onWorkflowComplete();
  }, [artworkForm, urlFlags, onWorkflowComplete]);

  const handleCanvaComplete = useCallback(
    async (file: File) => {
      setIsCanvaOpen(false);
      try {
        const { valid, message } = await validateDesignLabArtworkFile(file);
        if (!valid) {
          throw new Error(message || "Invalid file");
        }
        const uploadSide = (urlFlags?.transientData.side || "front") as Side;
        await uploadArtwork({ file, side: uploadSide, source: "canva" });
        onWorkflowComplete();
      } catch (error) {
        onWorkflowError(error instanceof Error ? error : new Error("Canva upload failed"));
      }
    },
    [uploadArtwork, urlFlags, onWorkflowComplete, onWorkflowError]
  );

  const handleCanvaCancel = useCallback(() => {
    setIsCanvaOpen(false);
    if (originalArtworkRef.current) {
      const targetSide = urlFlags?.transientData.side === "back" ? "back" : "front";
      if (targetSide === "back") {
        artworkForm.setValue("back", originalArtworkRef.current.back);
      } else {
        artworkForm.setValue("front", originalArtworkRef.current.front);
        artworkForm.setValue("frontLeftChest", originalArtworkRef.current.frontLeftChest);
      }
    }
    onWorkflowComplete();
  }, [artworkForm, urlFlags, onWorkflowComplete]);

  const handleAiComplete = useCallback(() => {
    setIsAiDesignOpen(false);
    onWorkflowComplete();
  }, [onWorkflowComplete]);

  useEffect(() => {
    if (isReady && activeWorkflow !== "none" && !hasProcessedRef.current) {
      processWorkflow();
    }
  }, [isReady, activeWorkflow, processWorkflow]);

  return {
    activeWorkflow,
    isProcessing,
    processWorkflow,
    isDesignLibraryOpen,
    isCanvaOpen,
    isAiDesignOpen,
    handleLibrarySelection,
    handleLibraryCancel,
    handleCanvaComplete,
    handleCanvaCancel,
    handleAiComplete,
    autoUploadFile,
    existingArtworkId
  };
}
